import { z } from "zod";

import {
  FieldConfig,
  getMaterialDimensionsFieldsConfiguration,
  PartTypeSchema,
  PartTypeType,
} from "shared";

const dimensionsSchema = z.object({
  thickness: z.number().optional(),
  diameter: z.number().optional(),
  height: z.number().optional(),
  width: z.number().optional(),
  length: z.number().optional(),
});

export type EditPartDimensions = z.infer<typeof dimensionsSchema>;

const editPartObjectSchema = z.object({
  name: z.string().trim().min(1),
  type: PartTypeSchema.nullable(),
  dimensions: dimensionsSchema,
});

type EditPartObjectValues = z.infer<typeof editPartObjectSchema>;

export const EditPartFormSchema = editPartObjectSchema.superRefine(
  (values: EditPartObjectValues, ctx) => {
    getMaterialDimensionsFieldsConfiguration(values.type).forEach((fieldConfig: FieldConfig) => {
      const value = values.dimensions[fieldConfig.key];
      const isInvalid = value === undefined || value <= 0;
      if (isInvalid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["dimensions", fieldConfig.key],
        });
      }
    });
  },
);

export type EditPartFormValues = z.infer<typeof EditPartFormSchema>;

type PartDimensionsFields = {
  height: number | null;
  width: number | null;
  length: number | null;
  tubeThickness: number | null;
};

type PartDimensionsPayload = {
  height?: number;
  width?: number;
  length?: number;
  tubeThickness?: number;
};

export const buildEditPartDimensionsDefaults = (
  type: PartTypeType | null,
  part: PartDimensionsFields,
): EditPartDimensions => {
  const defaults: EditPartDimensions = {};
  getMaterialDimensionsFieldsConfiguration(type).forEach((fieldConfig: FieldConfig) => {
    const value = part[fieldConfig.field];
    if (value !== null) {
      defaults[fieldConfig.key] = value;
    }
  });
  return defaults;
};

export const buildEditPartDimensionsPayload = (
  type: PartTypeType | null,
  dimensions: EditPartDimensions,
): PartDimensionsPayload => {
  const payload: PartDimensionsPayload = {};
  getMaterialDimensionsFieldsConfiguration(type).forEach((fieldConfig: FieldConfig) => {
    const value = dimensions[fieldConfig.key];
    if (value === undefined) {
      return;
    }
    payload[fieldConfig.field] = value;
    if (fieldConfig.syncWith) {
      payload[fieldConfig.syncWith] = value;
    }
  });
  return payload;
};
